import { TranslateService } from '@ngx-translate/core';

export class DateTimeUtility {
  /**
   * The function `getYears` returns an array of numbers representing the years between a start year and
   * an end year.
   * @param {number} startYear - The start year is the first year in the range of years you want to
   * generate.
   * @param {number} endYear - The `endYear` parameter represents the last year in the range of years you
   * want to generate.
   * @returns an array of numbers representing the years between the startYear and endYear (inclusive).
   */
  static getYears(startYear: number, endYear: number): number[] {
    const years = [];
    for (let i = startYear; i <= endYear; i++) {
      years.push(i);
    }
    return years;
  }

  /**
   * The function `getYearsInKeyVal` returns an array of objects containing the names and numbers of the years
   * @param startYear
   * @param endYear
   */
  static getYearsInKeyVal(startYear: number, endYear: number): { name: string; number: number }[] {
    const years = [];
    for (let i = startYear; i <= endYear; i++) {
      years.push({ name: i.toString(), number: i });
    }
    return years;
  }

  /**
   * The function `getMonths` returns an array of objects containing the names and numbers of the months,
   * with the option to use language-specific month names if available.
   * @returns The function `getMonths()` returns an array of objects, where each object represents a
   * month. Each object has two properties: `name` and `number`. The `name` property represents the name
   * of the month, and the `number` property represents the number of the month (1 for January, 2 for
   * February, and so on).
   */
  static getMonths(translateService?: TranslateService): { name: string; number: number }[] {
    const months = [
      {
        name: translateService ? translateService.instant('all') : 'All',
        number: 0,
      },
    ];
    for (let i = 0; i < 12; i++) {
      const name = new Date(0, i).toLocaleString('en', { month: 'long' });
      const txName = translateService ? translateService.instant(name.toLowerCase()) : name;
      months.push({
        name: txName,
        number: i + 1,
      });
    }
    return months;
  }

  /**
   * The function `getDays` returns an array of numbers representing the days of a given month and year,
   * with the current day marked as "Current".
   * @param translateService - The `translateService` parameter is an instance of the TranslateService required to translate the day names.
   * @param {number} [month] - The `month` parameter is an optional number that represents the month. If
   * not provided, it defaults to the current month.
   * @param {number} [year] - The `year` parameter is an optional number that represents the year for
   * which you want to get the days. If no value is provided, it defaults to the current year.
   * @param keyValue - The `keyValue` parameter is an optional boolean that determines whether the array returned is number or key value pair.
   * @returns an array of numbers or strings representing the days of the specified month and year.
   */
  static getDays(translateService: TranslateService, month?: number, year?: number, keyValue = true): (number | string | { name: string; number: number })[] {
    const now = new Date();
    month = month ?? now.getMonth(); // 0-indexed
    year = year ?? now.getFullYear();

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysArray: (number | string | { name: string; number: number })[] = [
      {
        name: translateService ? translateService.instant('all') : 'All',
        number: 0,
      },
    ];

    for (let i = 1; i <= daysInMonth; i++) {
      const isToday = i === now.getDate() && month === now.getMonth() && year === now.getFullYear();

      if (keyValue) {
        const dayObj = {
          name: isToday ? translateService.instant('current') : i.toString().toLowerCase(),
          number: i,
        };
        daysArray.push(dayObj);
      } else {
        if (isToday) {
          daysArray.push(translateService.instant('current'));
        } else {
          daysArray.push(i);
        }
      }
    }
    return daysArray;
  }

  /**
   * The function `getWeekDays` returns an array of objects containing the names and numbers of the days
   * @param translateService
   * @param startWith - The `startWith` parameter determines the starting day of the week, either 'monday' or 'sunday'.
   */
  static getWeekDays(translateService: TranslateService, startWith: 'monday' | 'sunday' = 'sunday'): { name: string; number: number }[] {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    const startIdx = startWith === 'monday' ? 1 : 0;

    const orderedDays = [...days.slice(startIdx), ...days.slice(0, startIdx)];

    return orderedDays.map((day) => ({
      name: translateService.instant(day),
      number: days.indexOf(day),
    }));
  }

  static convertWeekdayNameToNumber(weekdayName: string): number {
    const days = [
      { id: 0, name: ['Sonntag', 'Sunday', 'dimanche', 'domenica', 'Воскресенье', 'domingo', 'Pazar'] },
      { id: 1, name: ['Montag', 'Monday', 'lundi', 'lunedì', 'понедельник', 'lunes', 'Pazartesi'] },
      { id: 2, name: ['Dienstag', 'Tuesday', 'mardi', 'martedì', 'вторник', 'martes', 'Salı'] },
      { id: 3, name: ['Mittwoch', 'Wednesday', 'mercredi', 'mercoledì', 'среда', 'miércoles', 'Çarşamba'] },
      { id: 4, name: ['Donnerstag', 'Thursday', 'jeudi', 'giovedì', 'четверг', 'jueves', 'Perşembe'] },
      { id: 5, name: ['Freitag', 'Friday', 'vendredi', 'venerdì', 'Пятница', 'viernes', 'Cuma'] },
      { id: 6, name: ['Samstag', 'Saturday', 'samedi', 'sabato', 'Суббота', 'sábado', 'Cumartesi'] },
    ];
    const normalizedWeekday = weekdayName.toLowerCase();
    const day = days.find((d) => d.name.some((name) => name.toLowerCase() === normalizedWeekday));
    return day ? day.id : -1;
  }

  /**
   * The function `getDay` returns the day of the month for a given date.
   * @param date
   * @param translateService
   */
  static getDay(date: string, translateService?: TranslateService): string {
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    if (translateService) {
      return translateService.instant(day.toString().toLowerCase());
    }
    return `${day}`;
  }

  /**
   * The function `getMonth` returns the month name for a given date.
   * @param date
   * @param translateService
   */
  static getMonth(date: string, translateService?: TranslateService): string {
    const dateObj = new Date(date);
    const month = dateObj.getMonth();
    const monthName = new Date(0, month).toLocaleString('en', { month: 'long' });
    if (translateService) {
      return translateService.instant(monthName.toLowerCase());
    }
    return monthName;
  }

  /**
   * The function `getMonthNumber` returns the month number for a given date.
   * @param date
   */
  static getMonthNumber(date: string): number {
    const dateObj = new Date(date);
    return dateObj.getMonth() + 1;
  }

  /**
   * The function `getMonthName` returns the month name for a given month number.
   * @param month
   * @param translateService (optional)
   */
  static getMonthName(month: number, translateService?: TranslateService): string {
    const monthName = new Date(0, month - 1).toLocaleString('en', { month: 'long' });
    return translateService ? translateService.instant(monthName.toLowerCase()) : monthName;
  }

  /**
   * The function `getDateOnly` returns the date part of a given date string.
   * @param date
   */
  static getDateOnly(date: string): string {
    const dateObj = new Date(date);
    return dateObj.toISOString().split('T')[0];
  }

  /**
   * The function `getRemainingTime` returns the remaining time between the current time and a given end date.
   * @param endDateStr
   * @param translateService
   */
  static getRemainingTime(endDateStr: string, translateService?: TranslateService): string {
    const endDate = new Date(endDateStr);
    const currentTime = new Date();
    const remainingTime = endDate.getTime() - currentTime.getTime();

    if (remainingTime <= 0) {
      // Assuming 'Expired' or its equivalent is also defined within your translations
      return translateService ? translateService.instant('dates&filters.expired') : 'Expired';
    }

    // Helper function to translate labels
    const translateLabel = (label: string, count: number): string => {
      if (translateService) {
        const translationKey = `dates&filters.${label}${count === 1 ? '' : 's'}`; // Adjusted to match your object structure
        return `${count} ${translateService.instant(translationKey)}`;
      } else {
        return `${count} ${label}${count === 1 ? '' : 's'}`;
      }
    };

    const millisecondsPerMinute = 1000 * 60;
    const millisecondsPerHour = millisecondsPerMinute * 60;
    const millisecondsPerDay = millisecondsPerHour * 24;
    const millisecondsPerMonth = millisecondsPerDay * 30;
    const millisecondsPerYear = millisecondsPerDay * 365;

    const years = Math.floor(remainingTime / millisecondsPerYear);
    if (years > 0) {
      return translateLabel('year', years);
    }

    const months = Math.floor(remainingTime / millisecondsPerMonth);
    if (months > 0) {
      return translateLabel('month', months);
    }

    const days = Math.floor(remainingTime / millisecondsPerDay);
    if (days > 0) {
      return translateLabel('day', days);
    }

    const hours = Math.floor(remainingTime / millisecondsPerHour);
    if (hours > 0) {
      return translateLabel('hour', hours);
    }

    const minutes = Math.floor(remainingTime / millisecondsPerMinute);
    return translateLabel('minute', minutes);
  }

  /**
   * The function `getTimeAgo` returns a string indicating the time elapsed since a given date.
   * @param date
   * @param translateService
   */
  static getTimeAgo(date: string, translateService?: TranslateService): string {
    const createdAtDate = new Date(date);
    const now = new Date();

    // Calculate the difference in milliseconds
    const differenceMs = now.getTime() - createdAtDate.getTime();

    // Check if the timestamp is in the future
    if (differenceMs < 0) {
      return translateService ? translateService.instant('in the future') : 'in the future';
    }

    // Calculate the difference in minutes
    const differenceMinutes = Math.round(differenceMs / (1000 * 60));

    // Helper function to translate time units
    const translate = (key: string, value: string | number) => (translateService ? `${translateService.instant(key, { count: value })}` : `${value} ${key}`);

    // Return a string indicating the time elapsed
    if (differenceMinutes < 1) {
      return translate('just now', '');
    } else if (differenceMinutes < 60) {
      return translate('m ago', differenceMinutes);
    } else if (differenceMinutes < 1440) {
      const hours = Math.floor(differenceMinutes / 60);
      return translate('h ago', hours);
    } else if (differenceMinutes < 10080) {
      const days = Math.floor(differenceMinutes / 1440);
      return translate('d ago', days);
    } else if (differenceMinutes < 43800) {
      const weeks = Math.floor(differenceMinutes / 10080);
      return translate('w ago', weeks);
    } else {
      const months = Math.floor(differenceMinutes / 43800);
      return translate('mo ago', months);
    }
  }

  /**
   * Converts a date string to a UTC Date object at midnight.
   * @param {string} dateStr - The date string to convert.
   * @returns {Date} - A Date object representing midnight UTC of the given date.
   */
  static formatDateToUTC(dateStr: string): Date {
    const date = new Date(dateStr);
    date.setUTCHours(0, 0, 0, 0); // Set hours, minutes, seconds, and milliseconds to zero in UTC.
    return date;
  }

  /**
   * Converts a date object to a UTC string in the format 'YYYYMMDDTHHMMSSZ'.
   * @param {string} dateStr
   * @returns {string}
   */
  static formatDateToUTCString(dateStr: string): string {
    const date = new Date(dateStr + 'T00:00:00Z'); // Append time part to ensure it's interpreted as UTC
    return date.toISOString().replace(/[-:]/g, '').slice(0, 15) + 'Z'; // Formats as YYYYMMDDTHHMMSSZ
  }

  /**
   * Formats a date string to a specified pattern (e.g., 'DD.MM.YYYY').
   * @param dateStr
   * @param pattern - The pattern to format the date string to. Defaults to 'DD.MM.YYYY'.
   * @returns {string}
   *
   * @example
   * formatDateToString('2022-01-01', 'DD.MM.YYYY') // '01.01.2022'
   *
   */
  static formatDateToString(dateStr: string, pattern = 'DD.MM.YYYY'): string {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return pattern.replace('DD', day.toString().padStart(2, '0')).replace('MM', month.toString().padStart(2, '0')).replace('YYYY', year.toString());
  }

  static isDateToday(date: string): boolean {
    const today = new Date();
    const dateObj = new Date(date);
    return today.toDateString() === dateObj.toDateString();
  }

  /**
   * Creates a JavaScript Date object from separate date and time strings.
   * This utility is particularly useful for merging custom date and time string.
   *
   * @param dateString - The date in ISO format (YYYY-MM-DD), e.g., "2025-05-05"
   * @param timeString - The time in 24-hour format (HH:MM), e.g., "15:00"
   * @returns A JavaScript Date object combining the provided date and time
   *
   * @example
   * ```typescript
   * // Create a date for May 5th, 2025 at 3:00 PM
   * const dateTime = createDateTime("2025-05-05", "15:00");
   *
   * // Use in Angular template with date pipe
   * // {{ createDateTime(item.date, item.time) | date:'medium' }}
   * ```
   *
   * @throws {Error} If the date string is not in valid ISO format (YYYY-MM-DD)
   * @throws {Error} If the time string is not in valid 24-hour format (HH:MM)
   */
  static createDateTime(dateString: string, timeString: string): Date {
    // Validate date format (basic check for YYYY-MM-DD pattern)
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!datePattern.test(dateString)) {
      throw new Error(`Invalid date format. Expected YYYY-MM-DD, got: ${dateString}`);
    }

    // Validate time format (basic check for HH:MM pattern)
    const timePattern = /^\d{2}:\d{2}$/;
    if (!timePattern.test(timeString)) {
      throw new Error(`Invalid time format. Expected HH:MM, got: ${timeString}`);
    }

    // Combine date and time strings into ISO 8601 format
    // Adding ":00" for seconds to create complete datetime string
    const isoString = `${dateString}T${timeString}:00`;

    // Create and return the Date object
    const date = new Date(isoString);

    // Additional validation to ensure the created date is valid
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date/time combination: ${dateString} ${timeString}`);
    }

    return date;
  }

  /**
   * Alternative version that handles timezone explicitly.
   * Creates a Date object in the local timezone.
   *
   * @param dateString - The date in ISO format (YYYY-MM-DD)
   * @param timeString - The time in 24-hour format (HH:MM)
   * @returns A JavaScript Date object in local timezone
   */
  static createLocalDateTime(dateString: string, timeString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    const [hours, minutes] = timeString.split(':').map(Number);

    // Note: Month is 0-indexed in JavaScript Date constructor
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  }

  /**
   * Generates an array of time slots with a 10-minute interval for a full day (24 hours).
   * @returns {string[]} An array of time slots in the format 'HH:MM'.
   */
  static timeSlotsWith15MinInterval(): string[] {
    const timeSlots: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        timeSlots.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return timeSlots;
  }
}
